import { proportionInk, proportionPercents } from "@/features/staff/reports/charts/proportion"
import type { MethodTotal } from "@/features/staff/reports/types"

/**
 * How the period's money split across payment rails.
 *
 * **Not a pie.** A pie needs a legend, and a legend is a colour-only key — the
 * thing WCAG AA forbids and the thing `ChartLegendContent` in the kit happens to
 * be (a bare `backgroundColor` swatch). A single `h-2` bar is also the shape the
 * design system already uses for proportion everywhere else on this screen, and
 * it costs 8px of vertical space on a phone instead of a 200px circle.
 *
 * The bar carries no text of its own. Widths come from an integer-cent split, so
 * they are geometry — and the exact figures stay where they already were, in the
 * labelled rows underneath, each with its own count and its own server-built
 * amount. Nothing here is re-derived into something a reader could quote.
 *
 * `gap-px` is what makes the segments legible without a hue: the hairline is the
 * `--muted` track showing through, and every step of the ink ramp clears 3:1
 * against it in both themes, so each boundary is as readable as the segment and
 * the bar survives grayscale.
 *
 * The bar is `aria-hidden`. The rows below already say everything it shows, in
 * order, and a screen reader reading three unlabelled boxes gains nothing.
 */
export function ReportMethodShareBar({ methods }: { methods: MethodTotal[] }) {
  if (methods.length === 0) return null

  const widths = proportionPercents(methods.map((method) => method.collected))
  if (widths.every((width) => width <= 0)) return null

  return (
    <div
      aria-hidden="true"
      className="flex h-2 w-full gap-px overflow-hidden rounded-full bg-muted"
      data-testid="report-by-method-share"
    >
      {methods.map((method, index) =>
        widths[index] <= 0 ? null : (
          <span
            key={method.paymentMethod}
            className={proportionInk(index)}
            style={{ width: `${widths[index]}%` }}
          />
        )
      )}
    </div>
  )
}
